// ─────────────────────────────────────────────────────────────────────────────
// Tarea 17, fix round 1 — extrae el paginado de `winnersApi.exportNominationsRaw`, que vivía
// duplicado VERBATIM entre `AdminWinners.tsx` (`exportarNominacionesCrudas`, Task 4.5) y
// `ShineTab.tsx` (`fetchTodasLasNominacionesCrudas`, Tarea 17): mismo lote, misma condición de
// corte, mismo comentario de justificación. Un único lugar para todos los consumidores.
//
// 2026-10-19 — ningún export crudo tiene tope de filas: se recorre hasta la primera página vacía.
// Brillamos Juntos lo hacía desde RPT-6 (2026-10-07); ahora también Admin › Ganadores y Reco Diario
// (`fetchRecognitionsCrudas.ts`). Antes cortaban en 5000 filas con aviso.
// ─────────────────────────────────────────────────────────────────────────────
import { winnersApi } from '@/api/recognition';
import type { FiltrosDeDetalle, NominationRawExportRowDto } from '@/api/recognition';

const LOTE_NOMINACIONES_CRUDAS = 200;

/**
 * Salvaguarda, no tope: si un backend ignorara `page` y devolviera siempre la misma página, el
 * recorrido no terminaría nunca y colgaría el navegador. Al llegar aquí se LANZA un error (el
 * llamador lo muestra como fallo de exportación), nunca se corta en silencio. 50 000 páginas de 200
 * son 10 millones de filas, muy por encima de cualquier export real.
 */
const MAX_PAGINAS = 50_000;

/** Opciones del paginado. */
export interface OpcionesDePaginado {
  /**
   * 2026-10-09 — filtros de la tira (persona, Fundamental, modalidad, Site) para que el detalle del
   * libro de Brillamos Juntos cuadre con sus KPIs. Ganadores no los manda: exporta la campaña entera.
   */
  filtros?: FiltrosDeDetalle;
}

/**
 * Recorre un export paginado hasta la primera página vacía y devuelve TODAS las filas. Corta SOLO por
 * página vacía — NUNCA comparando `filas.length` contra `res.total`: `total` cuenta NOMINACIONES o
 * RECONOCIMIENTOS, no filas (`NominationsRawExportPageDto`, doc comment en `types.ts`), y uno grupal
 * aporta una fila por CANDIDATO, así que `filas.length` puede alcanzar o superar `total` mucho antes
 * de haber pedido todas las páginas.
 *
 * NO atrapa errores de red/HTTP: el llamador decide cómo mostrarlos.
 */
export async function recorrerTodasLasPaginas(
  pedirPagina: (pagina: number) => Promise<{ filas: NominationRawExportRowDto[] }>,
): Promise<NominationRawExportRowDto[]> {
  const filas: NominationRawExportRowDto[] = [];
  let pagina = 1;
  let res = await pedirPagina(pagina);
  while (res.filas.length > 0) {
    filas.push(...res.filas);
    pagina += 1;
    if (pagina > MAX_PAGINAS) {
      throw new Error(`El export superó ${MAX_PAGINAS} páginas sin terminar; se detuvo para no colgar el navegador.`);
    }
    res = await pedirPagina(pagina);
  }
  return filas;
}

/**
 * Sesión 2026-10-02 (Error 2) — mismo paginado sobre `winnersApi.exportReceivedRaw`: lo recibido por la
 * OU en todas las campañas Brillamos del periodo.
 */
export function fetchRecibidosPaginado(
  filtros: { ouIds: readonly string[]; from?: string; to?: string },
  { filtros: deLaTira = {} }: OpcionesDePaginado = {},
): Promise<NominationRawExportRowDto[]> {
  return recorrerTodasLasPaginas(
    (pagina) => winnersApi.exportReceivedRaw({ ...filtros, ...deLaTira }, pagina, LOTE_NOMINACIONES_CRUDAS),
  );
}

/** Pagina `winnersApi.exportNominationsRaw` completo para una campaña. */
export function fetchNominacionesCrudasPaginado(
  campaignId: string,
  { filtros = {} }: OpcionesDePaginado = {},
): Promise<NominationRawExportRowDto[]> {
  return recorrerTodasLasPaginas(
    (pagina) => winnersApi.exportNominationsRaw(campaignId, pagina, LOTE_NOMINACIONES_CRUDAS, filtros),
  );
}

/**
 * 2026-10-14 (BJT-1) — el export crudo de VARIAS campañas («Todas las campañas» de Brillamos Juntos):
 * el mismo recorrido por cada una, unido y ordenado por fecha. Una nominación pertenece a una sola
 * campaña, así que no hay duplicados. Con una sola campaña es exactamente `fetchNominacionesCrudasPaginado`.
 */
export async function fetchNominacionesDeLasCampanas(
  campaignIds: readonly string[],
  opciones: OpcionesDePaginado = {},
): Promise<NominationRawExportRowDto[]> {
  if (campaignIds.length === 1) return fetchNominacionesCrudasPaginado(campaignIds[0], opciones);

  const filas: NominationRawExportRowDto[] = [];
  for (const campaignId of campaignIds) {
    filas.push(...await fetchNominacionesCrudasPaginado(campaignId, opciones));
  }
  filas.sort((a, b) => a.date.localeCompare(b.date) || (a.recognitionId ?? '').localeCompare(b.recognitionId ?? ''));
  return filas;
}
